/**
 * Lesson phase helpers.
 *
 * There is one phase vocabulary now — `SessionLessonPhase`, the set stored in
 * Firestore — and both players use it directly. This file used to be `phaseMap`
 * and held translation tables between that set and a second vocabulary local to
 * XRLessonPlayerV3; unifying the names removed the translation and, with it, a
 * class of desynchronisation between what a player thought the phase was and
 * what the class was told.
 */

import type { SessionLessonPhase } from '../../types/lms';

/**
 * Phases that describe the player's own startup rather than a point in the
 * lesson. They must never be broadcast to a class or used to lock a student.
 */
export function isTransientPhase(phase: SessionLessonPhase | string | null | undefined): boolean {
  return !phase || phase === 'idle' || phase === 'loading';
}

/**
 * Phases a class may be locked to: what the teacher broadcasts and what a
 * student follows.
 *
 * `completed` is left out on purpose. It is not a point in the lesson the
 * teacher leads the class to; it is where a student arrives on their own by
 * handing in the quiz. While it was followable, a teacher who answered the quiz
 * on their own panel broadcast `completed`, and every student was dropped onto
 * "Lesson Complete" mid-quiz, their answers unsent, and counted as finished by
 * a gate that read `phase === 'completed'` as done. Students still reach
 * `completed` themselves; only the lockstep ignores it.
 */
export function isFollowablePhase(phase: SessionLessonPhase | string | null | undefined): boolean {
  return !isTransientPhase(phase) && phase !== 'completed';
}
